import cn from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Mousewheel, FreeMode } from 'swiper'
import Image from 'next/image'

import { formatDateDomini } from '@lib/utils'
import { Box } from '@features/story/components/Box'
import { UserAvatar } from '@components/UserAvatar'
import { bookTypeValue } from '@interfaces/BookEnum'
import { ArrowLeftIcon } from '@components/icons/ArrowLeftIcon'
import { CheckIcon } from '@components/icons/CheckIcon'
import { TagIcon } from '@components/icons/TagIcon'
import { ReportButton } from '@components/ReportButton'
import { CoverBadge } from '@features/story/components/CoverBadge'
import { CoverProps } from './interface'
import { ReadPromotion } from '../ReadPromotion'
import CoverMenuSection from '../CoverMenuSection'

export const Cover = ({
  coverImgPath,
  title,
  bookType,
  writingType,
  publishedAt,
  publishedUntil,
  cover,
  penName,
  tags,
  category,
  ratingName,
  writer,
  isEnded,
  isTranslated,
  isFollowedPenName,
  isToggleFollowPenNameLoading,
  canFollow,
  isShowBookPromotion,
  openReportModal,
  handleFollowPenName,
  handleGoToSearchTagPage,
  buyAllChaptersPromotion,
  readFreePromotion,
  bookBadges,
  bookChapterCampaignHeader,
  ...props
}: CoverProps) => {
  const router = useRouter()

  return (
    <Box className='card p-[30px] mobile:p-0 dark:border-purple'>
      <div className='flex mobile:flex-col space-x-[30px] mobile:space-x-0'>
        <div className='relative desktop-tablet:w-[379px] desktop-tablet:h-[532.97px] shrink-0 mobile:w-full desktop-tablet:rounded-lg overflow-hidden mobile:pt-[140.625%] bg-gray-3 dark:bg-dark-9 text-[18px]'>
          {coverImgPath ? (
            <Image src={coverImgPath} alt={title} layout='fill' />
          ) : null}

          <CoverBadge
            hasSalePromotions={isShowBookPromotion}
            bookBadges={bookBadges}
            buyAllChaptersPromotion={buyAllChaptersPromotion}
            readFreePromotion={readFreePromotion}
            bookChapterCampaignHeader={bookChapterCampaignHeader}
          />
        </div>
        <button
          onClick={() => router.back()}
          className='p-[8px] w-[40px] h-[40px] rounded-full bg-white border border-gray-4 dark:b z-10 absolute top-[40px] left-[20px] desktop-tablet:hidden'
        >
          <ArrowLeftIcon
            className='text-secondary dark:text-dark-10'
            width='24'
            height='24'
          />
        </button>

        <div className='mobile:px-[20px] mobile:pt-[20px] flex-1'>
          <div className='flex'>
            <h1 className='font-mitr font-medium text-secondary dark:text-dark-10 text-2xl flex items-center break-all mobile:w-full min-h-[38px]'>
              {title}
            </h1>
            <div
              className={cn('flex flex-1', {
                'desktop-tablet:justify-between': isEnded,
                'desktop-tablet:justify-end': !isEnded,
              })}
            >
              {isEnded && (
                <Box.Tag className='shrink-0 font-sarabun ml-[10px] tablet-mobile:hidden mt-[7px]'>
                  จบแล้ว
                </Box.Tag>
              )}
              <div className='tablet-mobile:hidden'>
                <ReportButton reportModalHandler={openReportModal} />
              </div>
            </div>
          </div>
          <div className='flex justify-between mt-[10px]'>
            <div className='flex flex-wrap space-x-[10px] font-bold text-[14px] text-secondary-1 items-center'>
              <div className='pr-[10px] border-r border-r-gray-3 dark:border-r-purple'>
                {bookTypeValue[bookType]}
              </div>
              <div className='pr-[10px] border-r border-r-gray-3 dark:border-r-purple'>
                {isTranslated ? 'นิยายแปล' : writingType}
              </div>
              <div className='pr-[10px] border-r border-r-gray-3 dark:border-r-purple'>
                {ratingName}
              </div>
              <Link
                href={`/category?id=${category.id}&bookType=${bookType}`}
                prefetch={false}
              >
                <a className='text-primary underline font-light'>
                  {category.name}
                </a>
              </Link>
            </div>
            <div className='desktop:hidden'>
              <ReportButton reportModalHandler={openReportModal} />
            </div>
          </div>
          <div className='flex flex-wrap items-center mt-[20px] tablet-mobile:flex-col tablet-mobile:space-[10px] tablet-mobile:items-start mobile:mt-[10px]'>
            <div className='flex flex-wrap  items-center gap-x-[10px]'>
              <div className='flex items-center space-x-[10px] tablet-mobile:mb-[10px]'>
                <UserAvatar
                  className='w-[40px] h-[40px] shrink-0'
                  slug={penName.user.profilePageSlug}
                  src={penName.user.avatarImgPath}
                />

                <Link
                  href={`/profile/${penName.user.profilePageSlug}?tab=penName&text=${penName.firstPenName}`}
                  prefetch={false}
                >
                  <a className='tracking-[0.13px] leading-[2] font-bold text-secondary dark:text-dark-10 hover:underline break-all line-clamp-1'>
                    {penName.firstPenName}
                  </a>
                </Link>

                {canFollow && (
                  <button
                    type='button'
                    className={cn(
                      'h-[30px] border rounded-[15px] flex justify-center items-center w-[103px] shrink-0',
                      {
                        'border-primary': isFollowedPenName,
                        'border-secondary-1 dark:border-dark-10':
                          !isFollowedPenName,
                      }
                    )}
                    onClick={handleFollowPenName}
                    disabled={isToggleFollowPenNameLoading}
                  >
                    <Box.Title
                      className={cn('text-sm flex items-center', {
                        'text-primary': isFollowedPenName,
                        'text-secondary-1 dark:text-dark-10':
                          !isFollowedPenName,
                      })}
                    >
                      {isFollowedPenName && (
                        <CheckIcon
                          className='mr-[10px]'
                          width='16'
                          height='16'
                        />
                      )}
                      ติดตาม
                    </Box.Title>
                  </button>
                )}
              </div>
              {!!publishedUntil && (
                <div className='flex flex-row'>
                  <Box.Text className='h-fit whitespace-nowrap text-xs bg-red-3 py-1 rounded px-2 text-white dark:text-white-2 font-semibold tablet-mobile:mb-[10px]'>
                    เรื่องนี้จะเผยแพร่ถึง{' '}
                    {formatDateDomini(publishedUntil, true)}
                  </Box.Text>
                </div>
              )}
            </div>
            {!isTranslated && (
              <>
                <div className='border-l h-[20px] border-gray-3 dark:border-purple mx-5 tablet-mobile:hidden' />

                <div className='flex justify-between items-center tablet-mobile:w-full'>
                  <Box.Text className='text-sm whitespace-nowrap dark:text-dark-10'>
                    เผยแพร่เมื่อ {formatDateDomini(publishedAt)}
                  </Box.Text>

                  {isEnded && (
                    <Box.Tag className='shrink-0 ml-[10px] desktop:hidden dark:text-dark-10'>
                      จบแล้ว
                    </Box.Tag>
                  )}
                </div>
              </>
            )}
            {/* TODO: wait story notification api */}
            {/* <div className='desktop:border-l h-[30px] desktop:border-gray-3  desktop:mx-5' /> */}
            {/* <button
              type='button'
              className='flex justify-center items-center w-[30px] h-[30px] border border-gray-4 rounded-lg p-0'
            >
              <NotificationOutlineIcon
                className='text-secondary-1'
                width='20'
                height='20'
              />
            </button> */}
          </div>
          {isTranslated && (
            <div className='flex items-center desktop:mt-[20px] tablet-mobile:flex-col'>
              <div className='flex desktop:justify-between items-center tablet-mobile:w-full tablet-mobile:mb-[10px]'>
                <Box.Text className='shrink-0 text-sm text-secondary-1'>
                  ผู้เขียน :
                </Box.Text>

                <Box.Text className='ml-[10px] text-sm break-all line-clamp-1 leading-[1.75] dark:text-dark-10'>
                  {writer}
                </Box.Text>
              </div>
              <div className='border-l h-[20px] border-gray-3 dark:border-purple mx-5 tablet-mobile:hidden' />
              <div className='flex justify-between items-center tablet-mobile:w-full'>
                <Box.Text className='text-sm whitespace-nowrap dark:text-dark-10'>
                  เผยแพร่เมื่อ {formatDateDomini(publishedAt)}
                </Box.Text>

                {isEnded && (
                  <Box.Tag className='shrink-0 ml-[10px] desktop:hidden dark:text-dark-10'>
                    จบแล้ว
                  </Box.Tag>
                )}
              </div>
            </div>
          )}
          <ReadPromotion isTopBanner />
          <div className='font-light  dark:text-dark-10 mt-[20px] break-word'>
            {cover}
          </div>
          <div className='tablet:hidden'>
            <CoverMenuSection {...props} />
          </div>
        </div>
      </div>
      <div className='desktop:hidden mobile:hidden'>
        <CoverMenuSection {...props} />
      </div>
      {!!tags.length && (
        <>
          <hr className='bg-gray-3 dark:bg-purple h-[1px] my-[30px] border-0 mobile:my-[20px] mobile:mx-[20px]' />

          <div className='flex items-center justify-center space-x-[10px] mobile:mx-[20px]'>
            <TagIcon
              className='text-primary min-w-[24px] mr-[10px]'
              width='24'
              height='24'
            />
            <Swiper
              className='!w-max !mx-[0px]'
              slidesPerView='auto'
              spaceBetween={10}
              modules={[Mousewheel, FreeMode]}
              mousewheel
              freeMode
            >
              {tags.map(tag => (
                <SwiperSlide key={tag.id}>
                  <button
                    key={tag.id}
                    type='button'
                    onClick={() => handleGoToSearchTagPage(tag.name, tag.id)}
                    className='bg-gray-3 dark:bg-dark-13 px-[10px] py-[4px] rounded-lg h-[30px]'
                  >
                    <span className='font-light text-sm text-secondary-1 dark:text-dark-10 whitespace-nowrap'>
                      {tag.name}
                    </span>
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </>
      )}
    </Box>
  )
}
